interface RateLimiterConfig {
  maxRequests: number;
  windowMs: number;
  storageKey?: string;
}

interface StoredState {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private readonly maxRequests: number;
  private readonly windowMs: number;
  private readonly storageKey: string | null;

  constructor(config: RateLimiterConfig) {
    this.maxRequests = config.maxRequests;
    this.windowMs = config.windowMs;
    this.storageKey = config.storageKey ?? null;
  }

  private readState(): StoredState {
    if (this.storageKey) {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (raw) return JSON.parse(raw) as StoredState;
      } catch { /* ignore */ }
    }
    return { count: 0, resetTime: Date.now() + this.windowMs };
  }

  private writeState(state: StoredState): void {
    if (!this.storageKey) return;
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch { /* ignore */ }
  }

  check(): void {
    const now = Date.now();
    const state = this.readState();

    if (now > state.resetTime) {
      state.count = 0;
      state.resetTime = now + this.windowMs;
    }

    if (state.count >= this.maxRequests) {
      const waitSeconds = Math.ceil((state.resetTime - now) / 1000);
      throw new Error(
        `Rate limit exceeded. Maximum ${this.maxRequests} operations per ${this.windowMs / 1000}s. Please wait ${waitSeconds}s.`
      );
    }

    state.count++;
    this.writeState(state);
  }

  getStats(): { count: number; limit: number; resetIn: number } {
    const now = Date.now();
    const state = this.readState();
    const count = now > state.resetTime ? 0 : state.count;
    const resetIn = Math.max(0, Math.ceil((state.resetTime - now) / 1000));
    return { count, limit: this.maxRequests, resetIn };
  }

  reset(): void {
    const state = { count: 0, resetTime: Date.now() + this.windowMs };
    this.writeState(state);
  }
}

export const conversionRateLimiter = new RateLimiter({
  maxRequests: 5,
  windowMs: 60 * 1000,
  storageKey: 'rl_conversion',
});

export const feedbackRateLimiter = new RateLimiter({
  maxRequests: 3,
  windowMs: 60 * 1000,
  storageKey: 'rl_feedback',
});

export default RateLimiter;
